import { useMemo } from 'react';
import {
  TranslationsForm,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
} from '@/components/translations';
import { LanguageTabs } from '@/components/universal';
import type { BookTranslationData } from '../types';

const BOOK_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'title', label: 'Название', type: 'input', required: true },
  { name: 'authorDisplay', label: 'Автор (отображаемый)', type: 'input' },
  { name: 'description', label: 'Описание', type: 'richtext' },
];

interface BookTranslationsTabProps {
  value: BookTranslationData[];
  onChange: (next: BookTranslationData[]) => void;
  languages: TranslationLanguage[];
  activeLanguage: string;
  onLanguageChange: (code: string) => void;
}

/**
 * Таб перевода метаданных книги: title / authorDisplay / description по языкам.
 * Переиспользует общий `TranslationsForm`.
 */
export function BookTranslationsTab({
  value,
  onChange,
  languages,
  activeLanguage,
  onLanguageChange,
}: BookTranslationsTabProps) {
  // Преобразуем массив в TranslationData shape
  const translationData = useMemo<TranslationData>(() => {
    const data: TranslationData = {};
    for (const t of value) {
      data[t.languageCode] = {
        title: t.title,
        authorDisplay: t.authorDisplay ?? '',
        description: t.description ?? '',
      };
    }
    return data;
  }, [value]);

  const handleChange = (next: TranslationData) => {
    const result: BookTranslationData[] = [];
    for (const [code, fields] of Object.entries(next)) {
      const title = fields.title?.trim() ?? '';
      if (!title) {
        continue;
      }
      const existing = value.find((t) => t.languageCode === code);
      result.push({
        id: existing?.id,
        languageCode: code,
        title,
        authorDisplay: fields.authorDisplay?.trim() || null,
        description: fields.description || null,
      });
    }
    onChange(result);
  };

  const tabItems = useMemo(
    () =>
      languages.map((l) => ({
        code: l.code,
        label: l.nativeName || l.name,
        flagEmoji: l.flag || undefined,
      })),
    [languages],
  );

  if (tabItems.length === 0) {
    return (
      <div className="text-muted-foreground text-sm">Нет доступных языков</div>
    );
  }

  return (
    <div className="space-y-4">
      <LanguageTabs
        languages={tabItems}
        activeLanguage={activeLanguage}
        onLanguageChange={onLanguageChange}
      />
      <TranslationsForm
        fields={BOOK_TRANSLATION_FIELDS}
        value={translationData}
        onChange={handleChange}
        languages={languages}
        activeLanguage={activeLanguage}
        onLanguageChange={onLanguageChange}
      />
    </div>
  );
}
